/**
 * userStorage.js — SEUL module autorisé à toucher localStorage._user (vague 4 lot 3).
 *
 * Gate mécanique : grep-gates.sh G6 interdit getItem/setItem/removeItem('_user')
 * partout ailleurs dans src/ (ce fichier est la liste blanche). Avant : 42 lectures
 * JSON.parse(localStorage.getItem('_user')) éparpillées dans 25 fichiers + 17 écritures
 * dans 14 fichiers — chaque divergence de parsing/écriture était un bug de session
 * potentiel (cf. bugs historiques silent logout / nav figée post-login / isVerified
 * vs accessToken).
 *
 * Ce module est VOLONTAIREMENT sans dépendance (aucun import) :
 *  - store/index.js le consomme pour l'hydratation au boot (une seule lecture) sans
 *    cycle store → session → store ;
 *  - src/index.tsx (pré-apply dark mode anti-FOUC) le consomme avant le mount React.
 *
 * Qui consommer selon le besoin :
 *  - composant/hook React → useSelector(selectUser / selectAuthFlags)
 *    (src/store/selectors/auth.js) — le store est hydraté au boot et tenu à jour ;
 *  - code impératif hors render (transport HTTP, callbacks wallet, pré-boot)
 *    → readStoredUser()/getStoredAuthHeader() ici ;
 *  - écriture/purge de session → utils/auth/session.js (persistUser/clearUser :
 *    écrit ICI **et** dispatch la mise à jour du store, indissociablement).
 */
const USER_KEY = '_user';

/** Lecture unique et défensive : objet user, ou null (absent / JSON illisible / storage indispo). */
export function readStoredUser() {
	try {
		const raw = localStorage.getItem(USER_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch (_e) {
		return null;
	}
}

/** Écriture brute (réservée au module session + authenticate). */
export function writeStoredUser(user) {
	try {
		localStorage.setItem(USER_KEY, JSON.stringify(user));
	} catch (_e) { /* storage indisponible : silencieux (comportement historique) */ }
}

/**
 * Patch partiel du user stocké, UNIQUEMENT si une session existe (accessToken) —
 * miroir exact du garde historique du toggle de thème (header.jsx).
 */
export function patchStoredUser(patch) {
	const u = readStoredUser();
	if (u && u.accessToken) {
		writeStoredUser({ ...u, ...patch });
	}
}

/** Purge brute (réservée au module session + authenticate). */
export function removeStoredUser() {
	try {
		localStorage.removeItem(USER_KEY);
	} catch (_e) { /* silencieux */ }
}

/** Token de session courant ('' si visiteur). */
export function getStoredAccessToken() {
	const u = readStoredUser();
	return (u && u.accessToken) || '';
}

/** En-tête Authorization prêt à l'emploi ({} si visiteur) — ex-services/auth-header. */
export function getStoredAuthHeader() {
	const token = getStoredAccessToken();
	return token ? { Authorization: 'Bearer ' + token } : {};
}
